import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { FeedItem } from '../../types';
import './RideStoryCard.css';

interface RideStoryCardProps {
  item: FeedItem;
  onClick: () => void;
}

const RideStoryCard: React.FC<RideStoryCardProps> = ({ item, onClick }) => {
  const { t } = useLanguage();

  const formatDistance = (meters?: number) => {
    if (!meters) return '0 km';
    return `${(meters / 1000).toFixed(0)} km`;
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '0h';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const timeAgo = (timestamp?: string) => {
    if (!timestamp) return '';
    const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (diff < 60) return t('feed.timeAgo.now');
    if (diff < 3600) return t('feed.timeAgo.minutes').replace('{{count}}', String(Math.floor(diff / 60)));
    if (diff < 86400) return t('feed.timeAgo.hours').replace('{{count}}', String(Math.floor(diff / 3600)));
    return t('feed.timeAgo.days').replace('{{count}}', String(Math.floor(diff / 86400)));
  };

  return (
    <div className="ride-story-card" onClick={onClick}>
      <div className="ride-story-cover">
        {item.cover_image_url ? (
          <img src={item.cover_image_url} alt="" className="ride-story-cover-img" loading="lazy" />
        ) : (
          <div className="ride-story-cover-fallback" />
        )}
        <div className="ride-story-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          {t('feed.rideStory.badge')}
        </div>
      </div>

      <div className="ride-story-content">
        <div className="ride-story-title">{item.title || t('feed.rideStory.badge')}</div>
        <div className="ride-story-summary-line">
          {item.item_count || 0} {t('feed.rideStory.moments')}
          {' \u00b7 '}
          {item.participant_count || 0} {t('feed.rideStory.riders')}
          {' \u00b7 '}
          {formatDistance(item.total_distance)}
        </div>
        <div className="ride-story-meta">
          <span>{timeAgo(item.published_at || item.timestamp)}</span>
          {item.total_elevation != null && item.total_elevation > 0 && (
            <>
              <span>{'\u00b7'}</span>
              <span>{Math.round(item.total_elevation)} m {t('feed.elevation').toLowerCase()}</span>
            </>
          )}
          {item.duration_minutes != null && item.duration_minutes > 0 && (
            <>
              <span>{'\u00b7'}</span>
              <span>{formatDuration(item.duration_minutes)}</span>
            </>
          )}
        </div>
      </div>

      <div className="ride-story-action">
        <span className="ride-story-view-btn">{t('feed.rideStory.viewStory')}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </div>
  );
};

export default RideStoryCard;
